import React from "react";
import useReveal from "../../hooks/useReveal";
import "./styling/Reveal.css";

/**
 * Reveal — wraps any element/content so it fades + slides gently into view the
 * first time it scrolls onto screen. Purely presentational: it renders the tag
 * you ask for (default `div`) and forwards all extra props (onClick, aria-*,
 * etc.), so it can stand in for an existing element rather than nesting a new
 * one.
 *
 * Because the entrance uses a CSS transition on this wrapper only, it never
 * interferes with hover transitions on interactive children. Use `delay` to
 * stagger siblings (e.g. `delay={index * 70}`).
 *
 * Respects `prefers-reduced-motion` via {@link useReveal}.
 *
 * @param {Object}  props
 * @param {React.ElementType} [props.as="div"]  Element/tag to render.
 * @param {string}  [props.className]           Extra classes (layout, variants).
 * @param {number}  [props.delay=0]             Entrance delay in ms (stagger).
 * @param {Object}  [props.style]               Extra inline styles (merged).
 */
const Reveal = ({
  as: Tag = "div",
  className = "",
  delay = 0,
  style,
  children,
  ...rest
}) => {
  const [ref, isVisible] = useReveal();

  const mergedStyle =
    delay > 0 ? { transitionDelay: `${delay}ms`, ...style } : style;

  const classes = ["reveal", isVisible ? "is-visible" : "", className]
    .filter(Boolean)
    .join(" ");

  return (
    <Tag ref={ref} className={classes} style={mergedStyle} {...rest}>
      {children}
    </Tag>
  );
};

export default Reveal;
